import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './admin.css';

const LANGUAGES = [
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'en', name: 'English' },
];

const SubtitleGenerator = () => {
  const [bulkConfig, setBulkConfig] = useState({
    limit: 10,
    language: 'es',
    sourceLanguage: 'en',
  });
  
  const [singleVideoId, setSingleVideoId] = useState('');
  const [singleConfig, setSingleConfig] = useState({
    language: 'es',
    sourceLanguage: 'en',
  });
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    fetchStats(bulkConfig.language);
  }, [bulkConfig.language]);

  const fetchStats = async (language) => {
    setStatsLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_APP_API_URL}/admin/subtitles/stats?language=${language}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_URL}/admin/subtitles/generate`,
        bulkConfig,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setResults(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate subtitles');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleGenerate = async () => {
    if (!singleVideoId.trim()) {
      setError('Please enter a video ID');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_URL}/admin/subtitles/generate/${singleVideoId}`,
        singleConfig,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setResults({
        processed: 1,
        success: 1,
        errors: 0,
        skipped: 0,
        results: [response.data.data],
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate subtitles');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <h1 className="mb-4">Subtitle Generation Utility</h1>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title text-muted">Total Assets</h5>
              <div className="display-4">{statsLoading ? '...' : stats?.totalAssets || 0}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card text-center bg-success bg-opacity-10">
            <div className="card-body">
              <h5 className="card-title text-success">With {LANGUAGES.find(l => l.code === bulkConfig.language)?.name} Subtitles</h5>
              <div className="display-4 text-success">{statsLoading ? '...' : stats?.withSubtitles || 0}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card text-center bg-warning bg-opacity-10">
            <div className="card-body">
              <h5 className="card-title text-warning">Without {LANGUAGES.find(l => l.code === bulkConfig.language)?.name} Subtitles</h5>
              <div className="display-4 text-warning">{statsLoading ? '...' : stats?.withoutSubtitles || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Generation */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Bulk Subtitle Generation</h5>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label htmlFor="bulk-limit" className="form-label">Number of Videos</label>
              <input
                id="bulk-limit"
                type="number"
                className="form-control"
                min="1"
                max="100"
                value={bulkConfig.limit}
                onChange={(e) =>
                  setBulkConfig({ ...bulkConfig, limit: parseInt(e.target.value) })
                }
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="bulk-source" className="form-label">Source Language</label>
              <select
                id="bulk-source"
                className="form-control"
                value={bulkConfig.sourceLanguage}
                onChange={(e) =>
                  setBulkConfig({ ...bulkConfig, sourceLanguage: e.target.value })
                }
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="bulk-target" className="form-label">Target Language</label>
              <select
                id="bulk-target"
                className="form-control"
                value={bulkConfig.language}
                onChange={(e) =>
                  setBulkConfig({ ...bulkConfig, language: e.target.value })
                }
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button 
            className="btn btn-primary" 
            onClick={handleBulkGenerate} 
            disabled={loading}
          >
            {loading && <span className="spinner-border spinner-border-sm me-2" />}
            Generate Subtitles for Multiple Videos
          </button>
        </div>
      </div>

      {/* Single Video Generation */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Single Video Subtitle Generation</h5>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label htmlFor="video-id" className="form-label">Video ID</label>
              <input
                id="video-id"
                className="form-control"
                placeholder="e.g., v_123"
                value={singleVideoId}
                onChange={(e) => setSingleVideoId(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="single-source" className="form-label">Source Language</label>
              <select
                id="single-source"
                className="form-control"
                value={singleConfig.sourceLanguage}
                onChange={(e) =>
                  setSingleConfig({ ...singleConfig, sourceLanguage: e.target.value })
                }
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="single-target" className="form-label">Target Language</label>
              <select
                id="single-target"
                className="form-control"
                value={singleConfig.language}
                onChange={(e) =>
                  setSingleConfig({ ...singleConfig, language: e.target.value })
                }
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button 
            className="btn btn-primary" 
            onClick={handleSingleGenerate} 
            disabled={loading}
          >
            {loading && <span className="spinner-border spinner-border-sm me-2" />}
            Generate Subtitles for This Video
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">Results</h5>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="text-center p-3 bg-light rounded">
                  <div className="fs-2 fw-bold">{results.processed}</div>
                  <div className="text-muted">Processed</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                  <div className="fs-2 fw-bold text-success">{results.success}</div>
                  <div className="text-muted">Success</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 bg-danger bg-opacity-10 rounded">
                  <div className="fs-2 fw-bold text-danger">{results.errors}</div>
                  <div className="text-muted">Errors</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                  <div className="fs-2 fw-bold text-warning">{results.skipped}</div>
                  <div className="text-muted">Skipped</div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Video ID</th>
                    <th>Title</th>
                    <th>Asset ID</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((result, index) => (
                    <tr key={index}>
                      <td>{result.videoId}</td>
                      <td>{result.title}</td>
                      <td><code className="small">{result.assetId}</code></td>
                      <td>
                        <span
                          className={`badge ${
                            result.status === 'success'
                              ? 'bg-success'
                              : result.status === 'error'
                              ? 'bg-danger'
                              : 'bg-warning'
                          }`}
                        >
                          {result.status}
                        </span>
                      </td>
                      <td className="small">
                        {result.trackId && (
                          <span className="text-success">Track: {result.trackId}</span>
                        )}
                        {result.reason && (
                          <span className="text-warning">{result.reason}</span>
                        )}
                        {result.error && (
                          <span className="text-danger">{result.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Language Codes Reference */}
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Supported Languages</h5>
        </div>
        <div className="card-body">
          <div className="row g-2 small">
            {LANGUAGES.map(lang => (
              <div key={lang.code} className="col-md-4">
                <strong>{lang.code}</strong> - {lang.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubtitleGenerator;
